import React, { useState, useEffect } from 'react';
import { clinicService } from '../../services/api';
import { formatApiDetail } from '../../utils/formatApiDetail';

const ENTITY_TYPES = [
  { value: '', label: 'All' },
  { value: 'entity', label: 'Entity' },
  { value: 'branch', label: 'Branch' },
  { value: 'appointment_type', label: 'Appointment type' },
  { value: 'hint_summary', label: 'Hint summary' },
  { value: 'doctor', label: 'Doctor' },
  { value: 'service_name_mapping', label: 'Service name mapping' },
  { value: 'chief_complaint_service_mapping', label: 'Chief complaint service mapping' },
  { value: 'location', label: 'Location' },
  { value: 'doctor_location', label: 'Doctor location' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'online_booking_calendar', label: 'Online booking calendar' },
  { value: 'team', label: 'Team' },
  { value: 'team_member', label: 'Team member' },
];

const ENTITY_TYPE_FORM_OPTIONS = ENTITY_TYPES.filter((o) => o.value !== '');

const INTEGRATION_OPTIONS = [
  { value: '', label: '—' },
  { value: 'sgi_med', label: 'SGI Med' },
  { value: 'sg_clinic_assist', label: 'SG Clinic Assist' },
  { value: 'nexi_support', label: 'Nexi Support' },
];

const getMetaDataSchemaKey = (type, integration) => `${type || 'entity'}|${integration || ''}`;

const DEFAULT_META_DATA_SCHEMA = { type: 'object', properties: {}, additionalProperties: true };

const EntitiesSection = ({ clinicId, onDataChange }) => {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [metaDataSchemas, setMetaDataSchemas] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    type: 'entity',
    is_active: true,
    external_code: '',
    integration: '',
    meta_data_json: '',
  });
  const [metaDataJsonError, setMetaDataJsonError] = useState(null);

  const fetchEntities = async () => {
    try {
      setLoading(true);
      setError(null);
      const typeParam = typeFilter || null;
      const data = await clinicService.getClinicEntities(clinicId, typeParam, includeInactive);
      setEntities(data.entities || []);
    } catch (err) {
      console.error('Error loading clinic entities:', err);
      setError(formatApiDetail(err.response?.data?.detail, 'Failed to load entities.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntities();
  }, [clinicId, typeFilter, includeInactive]);

  useEffect(() => {
    const fetchSchemas = async () => {
      try {
        const data = await clinicService.getEntityMetaDataSchemas();
        setMetaDataSchemas(data.schemas || null);
      } catch (err) {
        console.error('Error loading entity meta_data schemas:', err);
      }
    };
    fetchSchemas();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? e.target.checked : value,
    }));
    if (name === 'meta_data_json') {
      const trimmed = value.trim();
      if (!trimmed) {
        setMetaDataJsonError(null);
        return;
      }
      try {
        JSON.parse(trimmed);
        setMetaDataJsonError(null);
      } catch {
        setMetaDataJsonError('Invalid JSON');
      }
    }
  };

  const parseMetaData = () => {
    const raw = form.meta_data_json?.trim();
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return undefined;
    }
  };

  const handleAdd = async () => {
    if (!form.name || !form.name.trim()) {
      setError('Name is required.');
      return;
    }
    const metaData = parseMetaData();
    if (form.meta_data_json?.trim() && metaData === undefined) {
      setError('Metadata must be valid JSON.');
      return;
    }
    try {
      setIsSaving(true);
      setError(null);
      const payload = {
        name: form.name.trim(),
        description: form.description?.trim() || null,
        type: form.type,
        is_active: form.is_active,
        external_code: form.external_code?.trim() || null,
        integration: form.integration?.trim() || null,
        meta_data: metaData ?? null,
      };
      const data = await clinicService.createClinicEntity(clinicId, payload);
      setEntities((prev) => [...prev, data.entity]);
      resetForm();
      setShowAddForm(false);
      setSuccessMessage('Entity created successfully.');
      if (onDataChange) onDataChange();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error creating entity:', err);
      setError(formatApiDetail(err.response?.data?.detail, 'Failed to create entity.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (entity) => {
    setEditingEntity(entity);
    setForm({
      name: entity.name || '',
      description: entity.description || '',
      type: entity.type || 'entity',
      is_active: entity.is_active !== false,
      external_code: entity.external_code || '',
      integration: entity.integration || '',
      meta_data_json: entity.meta_data != null
        ? JSON.stringify(entity.meta_data, null, 2)
        : '',
    });
    setMetaDataJsonError(null);
    setShowAddForm(true);
  };

  const handleUpdate = async () => {
    if (!editingEntity) return;
    if (!form.name || !form.name.trim()) {
      setError('Name is required.');
      return;
    }
    const metaData = parseMetaData();
    if (form.meta_data_json?.trim() && metaData === undefined) {
      setError('Metadata must be valid JSON.');
      return;
    }
    try {
      setIsSaving(true);
      setError(null);
      const payload = {
        name: form.name.trim(),
        description: form.description?.trim() || null,
        type: form.type,
        is_active: form.is_active,
        external_code: form.external_code?.trim() || null,
        integration: form.integration?.trim() || null,
        meta_data: metaData ?? null,
      };
      const data = await clinicService.updateClinicEntity(clinicId, editingEntity.id, payload);
      setEntities((prev) =>
        prev.map((e) => (e.id === editingEntity.id ? data.entity : e))
      );
      resetForm();
      setEditingEntity(null);
      setShowAddForm(false);
      setSuccessMessage('Entity updated successfully.');
      if (onDataChange) onDataChange();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating entity:', err);
      setError(formatApiDetail(err.response?.data?.detail, 'Failed to update entity.'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeactivate = async (entity) => {
    if (!window.confirm(`Deactivate "${entity.name}"? It will be hidden from the list unless you show deactivated.`)) {
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const data = await clinicService.updateClinicEntity(clinicId, entity.id, { is_active: false });
      setEntities((prev) =>
        prev.map((e) => (e.id === entity.id ? (data.entity || { ...e, is_active: false }) : e))
      );
      setSuccessMessage('Entity deactivated.');
      if (onDataChange) onDataChange();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deactivating entity:', err);
      setError(formatApiDetail(err.response?.data?.detail, 'Failed to deactivate entity.'));
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async (entity) => {
    try {
      setLoading(true);
      setError(null);
      const data = await clinicService.updateClinicEntity(clinicId, entity.id, { is_active: true });
      setEntities((prev) =>
        prev.map((e) => (e.id === entity.id ? (data.entity || { ...e, is_active: true }) : e))
      );
      setSuccessMessage('Entity reactivated.');
      if (onDataChange) onDataChange();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error reactivating entity:', err);
      setError(formatApiDetail(err.response?.data?.detail, 'Failed to reactivate entity.'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      type: 'entity',
      is_active: true,
      external_code: '',
      integration: '',
      meta_data_json: '',
    });
    setMetaDataJsonError(null);
    setError(null);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingEntity(null);
    resetForm();
  };

  const formatDate = (d) => {
    if (!d) return '–';
    try {
      const date = typeof d === 'string' ? new Date(d) : d;
      return date.toLocaleString();
    } catch {
      return '–';
    }
  };

  if (loading && entities.length === 0) {
    return (
      <div className="flex justify-center p-8">
        <p className="text-gray-500">Loading entities...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <h3 className="text-lg font-semibold text-gray-700">Clinic Entities</h3>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm"
          >
            {ENTITY_TYPES.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="rounded border-gray-300"
            />
            Show deactivated
          </label>
          {!showAddForm && (
            <button
              onClick={() => {
                setShowAddForm(true);
                setEditingEntity(null);
                resetForm();
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center text-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Entity
            </button>
          )}
        </div>
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded whitespace-pre-wrap break-words">
          {error}
        </div>
      )}

      {showAddForm && (
        <div className="mb-6 p-6 border rounded-lg bg-gray-50">
          <h4 className="text-lg font-medium mb-4">
            {editingEntity ? 'Edit Entity' : 'Add New Entity'}
          </h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="Entity name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded px-3 py-2"
                rows={2}
                placeholder="Optional description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">External code</label>
              <input
                type="text"
                name="external_code"
                value={form.external_code}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="Optional external reference code"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Integration</label>
              <select
                name="integration"
                value={form.integration}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                {INTEGRATION_OPTIONS.map((opt) => (
                  <option key={opt.value || 'none'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                name="type"
                value={form.type}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                {ENTITY_TYPE_FORM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="form_is_active"
                name="is_active"
                checked={form.is_active}
                onChange={handleInputChange}
                className="mr-2"
              />
              <label htmlFor="form_is_active" className="text-sm text-gray-700">Active</label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Metadata (JSON)</label>
              <textarea
                name="meta_data_json"
                value={form.meta_data_json}
                onChange={handleInputChange}
                className={`w-full border rounded px-3 py-2 font-mono text-sm min-h-[120px] ${metaDataJsonError ? 'border-red-500' : 'border-gray-300'}`}
                placeholder='{}'
                spellCheck={false}
              />
              {metaDataJsonError && (
                <p className="mt-1 text-sm text-red-600">{metaDataJsonError}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">Optional. Valid JSON (object or root array, depending on entity type).</p>
              <details className="mt-2">
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Schema for {form.type || 'entity'}{form.integration ? ` + ${form.integration}` : ''}</summary>
                <pre className="mt-1 p-2 bg-gray-100 rounded text-xs font-mono overflow-x-auto max-h-32 overflow-y-auto">
                  {JSON.stringify(
                    metaDataSchemas
                      ? (metaDataSchemas[getMetaDataSchemaKey(form.type, form.integration)] ?? DEFAULT_META_DATA_SCHEMA)
                      : DEFAULT_META_DATA_SCHEMA,
                    null,
                    2
                  )}
                </pre>
              </details>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={editingEntity ? handleUpdate : handleAdd}
                disabled={isSaving}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : editingEntity ? 'Update' : 'Create'}
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">External code</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Integration</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {entities.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-500">
                  No entities found. Add one to get started.
                </td>
              </tr>
            ) : (
              entities.map((entity) => (
                <tr
                  key={entity.id}
                  className={entity.is_active === false ? 'bg-gray-50 opacity-90' : ''}
                >
                  <td className="px-4 py-2 text-sm text-gray-900">
                    {entity.name}
                    {entity.is_active === false && (
                      <span className="ml-2 text-xs font-medium text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate">{entity.description || '–'}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{entity.external_code || '–'}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{entity.integration || '–'}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{entity.type || '–'}</td>
                  <td className="px-4 py-2 text-sm">{entity.is_active ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-2 text-sm text-gray-500">{formatDate(entity.created_at)}</td>
                  <td className="px-4 py-2 text-right text-sm">
                    <button
                      onClick={() => handleEdit(entity)}
                      className="text-blue-600 hover:underline mr-3"
                    >
                      Edit
                    </button>
                    {entity.is_active === false ? (
                      <button
                        onClick={() => handleReactivate(entity)}
                        className="text-green-600 hover:underline"
                      >
                        Reactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => handleDeactivate(entity)}
                        className="text-amber-600 hover:underline"
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EntitiesSection;
